import React, { useMemo } from 'react';
import { Marker, Polyline, Tooltip } from 'react-leaflet';
import useGeometryEditing from './hooks/useGeometryEditing';

const MIN_RADIUS = 5;

/**
 * Crossline renderer: draws a line through the checkpoint center using radius + bearing.
 * Both endpoints are draggable and keep geometry symmetric.
 */
export default function CrosslineShape({
  cp,
  setSelectedCheckpointId,
  handleIcon,
  metersPerDegreeLat,
  metersPerDegreeLng,
  updateCheckpoint,
  suppressFocusNext,
  editing = true,
}) {
  const { geometry } = useGeometryEditing({
    cp,
    updateCheckpoint,
    defaultType: 'crossline',
  });
  const centerLat = Number(cp?.lat);
  const centerLng = Number(cp?.lng);
  const hasCenter = Number.isFinite(centerLat) && Number.isFinite(centerLng);
  const radius =
    Math.max(MIN_RADIUS, Math.round(geometry?.radiusMeters ?? cp?.radiusMeters ?? 75)) || 75;
  const bearing = Number.isFinite(
    geometry?.angleRad ?? geometry?.handleBearing ?? cp?.handleBearing,
  )
    ? (geometry?.angleRad ?? geometry?.handleBearing ?? cp?.handleBearing)
    : 0;

  const lngFactor = metersPerDegreeLng(centerLat || 0) || metersPerDegreeLat;
  const offsetPoint = (distance, angle) => ({
    lat: centerLat + (distance * Math.cos(angle)) / metersPerDegreeLat,
    lng: centerLng + (distance * Math.sin(angle)) / lngFactor,
  });

  const endpoints = useMemo(() => {
    if (!hasCenter) return [];
    const p1 = offsetPoint(radius, bearing);
    const p2 = offsetPoint(radius, bearing + Math.PI);
    return [p1, p2];
  }, [bearing, centerLat, centerLng, hasCenter, lngFactor, metersPerDegreeLat, radius]);

  if (!hasCenter || endpoints.length !== 2) return null;

  const applyEndpoints = (primaryPoint, indexMoved) => {
    const dLat = primaryPoint.lat - centerLat;
    const dLng = primaryPoint.lng - centerLng;
    const metersLat = dLat * metersPerDegreeLat;
    const metersLng = dLng * lngFactor;
    const nextRadius = Math.max(
      MIN_RADIUS,
      Math.round(Math.sqrt(metersLat * metersLat + metersLng * metersLng)),
    );
    const nextBearing = Math.atan2(metersLng, metersLat);
    const otherPoint = offsetPoint(nextRadius, nextBearing + Math.PI);
    const points = indexMoved === 0 ? [primaryPoint, otherPoint] : [otherPoint, primaryPoint];

    updateCheckpoint(cp.id, {
      radiusMeters: nextRadius,
      handleBearing: nextBearing,
      geometry: {
        ...geometry,
        type: 'crossline',
        radiusMeters: nextRadius,
        angleRad: nextBearing,
        handleBearing: nextBearing,
        points,
      },
    });
  };

  const handleDragFactory = (index) => ({
    drag: (e) => {
      suppressFocusNext.current = true;
      applyEndpoints({ lat: e.target.getLatLng().lat, lng: e.target.getLatLng().lng }, index);
    },
    click: () => {
      suppressFocusNext.current = true;
      setSelectedCheckpointId(cp.id);
    },
  });

  return (
    <>
      <Polyline
        positions={endpoints}
        pathOptions={{ color: '#2e7be7', weight: 2 }}
        eventHandlers={{
          click: () => setSelectedCheckpointId(cp.id),
        }}
      />
      {editing &&
        endpoints.map((pt, idx) => (
          <Marker
            key={`crossline-handle-${cp.id}-${idx}`}
            position={pt}
            draggable
            icon={handleIcon}
            eventHandlers={handleDragFactory(idx)}
          >
            <Tooltip className="responder-tooltip" direction="top" offset={[0, -10]} opacity={0.8}>
              Drag to resize/rotate
            </Tooltip>
          </Marker>
        ))}
    </>
  );
}
